/**
 * What state a thing is IN, and how it MOVES between states — the small
 * shared pieces that condition evaluation, catalog derivation, the effect
 * applier, and the handlers all have to agree about.
 *
 * @remarks
 * Extracted in D55 for a reason more forcing than tidiness: `container_open`
 * became an authored CONDITION, so `evaluate-condition` needs this reading —
 * and `derive-entity-catalog` already imports `evaluate-condition`, which
 * would have made the pair a cycle. A predicate two modules must share belongs
 * to neither of them.
 *
 * That is also the point of the exercise. "Is this thing open?" is now asked by
 * the catalog, by the door verbs, and by any author writing conditional text,
 * and all three get their answer from the one line below.
 *
 * @packageDocumentation
 */

import type { ItemDefinition, ItemId, RuntimeState } from '@orpheus/content-schema';

/**
 * Is this thing's door open right now (D50)?
 *
 * @remarks
 * `openedContainerItemIds` is the single source of truth for door state, and
 * genesis seeds it from every item authored `state: 'open'` — the same shape
 * `flagValues` has used since R5, where declarations set the starting value and
 * the runtime record carries it from there. A toggle-relative-to-authored set
 * was the alternative and it cannot answer "is it open?" without also knowing
 * what it started as, which is exactly the derived-data trap §3.2 forbids.
 *
 * A thing with no door is always open, because there is nothing to be shut.
 *
 * @param container - The thing that may have a door
 * @param state - The live runtime state
 * @returns Whether its contents are reachable through the door
 */
export const isContainerOpen = (container: ItemDefinition, state: RuntimeState): boolean =>
	container.openable === undefined || state.world.openedContainerItemIds.includes(container.itemId);

/**
 * Is this thing's door shut AND see-through (D253) — what it holds in sight
 * and out of reach?
 *
 * @remarks
 * The one reading the catalog (in sight), the reach walk (out of reach) and
 * `search` (named through the door) all share, for D55's reason.
 *
 * @param container - The thing that may have a door
 * @param state - The live runtime state
 * @returns Whether it is shut and its door is mesh or glass
 */
export const isShutSeeThrough = (container: ItemDefinition, state: RuntimeState): boolean =>
	container.openable?.isSeeThrough === true && !isContainerOpen(container, state);

/**
 * Has this CONCEALED thing been found (D239) — has the container it is hidden
 * in been gone through?
 *
 * @remarks
 * The one reading of a find, here for D55's reason: `item_found` is an
 * authored condition, so the evaluator needs it, and the entity catalog's
 * concealment gate needs the same answer — a condition that called a thing
 * found while the catalog still hid it would be two readings of one search.
 * There is no record of finds beside `searchedContainerItemIds`: a concealed
 * thing is found exactly when its container is in that set, so a find is as
 * durable as the search (kept when the thing is taken, rewound by the
 * checkpoint's return with the search itself).
 *
 * A thing that is not concealed was never hidden, so it is never found — the
 * loader refuses `item_found` on such a thing, because the condition could
 * never hold.
 *
 * D240: a thing a BODY carries is found when the body has been gone through
 * (`searchedActorIds`, the item set's twin) — the same fact from the same
 * act, read the same way by the catalog and by the condition.
 *
 * @param item - The thing that may be hidden inside another, or on a body
 * @param state - The live runtime state
 * @returns Whether it is concealed and its container has been searched
 */
export const isConcealedItemFound = (item: ItemDefinition, state: RuntimeState): boolean => {
	const { containedIn } = item;

	if (containedIn?.concealed !== true) {
		return false;
	}

	return 'containerActorId' in containedIn
		? state.world.searchedActorIds.includes(containedIn.containerActorId)
		: state.world.searchedContainerItemIds.includes(containedIn.containerItemId);
};

/**
 * Is this thing's switch on right now (D56)?
 *
 * @remarks
 * A thing with no switch is never on, which is the opposite default from
 * {@link isContainerOpen} and correct for the same reason: a thing with no
 * door has nothing standing between you and its contents, while a thing with
 * no switch has nothing that could be running.
 *
 * @param item - The thing that may have a switch
 * @param state - The live runtime state
 * @returns Whether it is currently switched on
 */
export const isSwitchedOn = (item: ItemDefinition, state: RuntimeState): boolean =>
	item.switchable !== undefined && state.world.switchedOnItemIds.includes(item.itemId);

/**
 * Is this thing spent right now (D152) — its `durability.uses` all gone?
 *
 * @remarks
 * A thing with no durability is never spent; a spent count of zero is the
 * same fact as no entry. Here rather than in the durability seam for D55's
 * reason exactly: `item_spent` is an authored condition, so the evaluator
 * needs the reading, and the seam imports the evaluator's world.
 *
 * @param item - The thing that may wear out
 * @param state - The live runtime state
 * @returns Whether it has been spent as many times as it can be
 */
export const isItemSpent = (item: ItemDefinition, state: RuntimeState): boolean =>
	item.durability !== undefined && (state.world.spentUses[item.itemId] ?? 0) >= item.durability.uses;

/**
 * The slot this item is worn in, if it is worn (D143).
 *
 * @param state - The live runtime state
 * @param itemId - The item
 * @returns The slot's name, or `undefined` when nothing wears the item
 */
export const slotWearing = (state: RuntimeState, itemId: ItemId): string | undefined =>
	Object.entries(state.world.equippedItemIds).find(([, wornItemId]) => wornItemId === itemId)?.[0];

/**
 * The state with this item out of whatever slot held it — untouched when it
 * held none (D143). `handleDrop` reads it: dropping a worn thing takes it off
 * first; since D230 the `remove_item` effect reads it too, because a thing
 * gone from the world cannot still be on the player. Here rather than in the
 * equip handlers because the effect applier needs it and the handlers' world
 * imports the applier's.
 *
 * @param state - The state before
 * @param itemId - The item
 * @returns The state with the slot freed
 */
export const unequipItem = (state: RuntimeState, itemId: ItemId): RuntimeState => {
	const slot = slotWearing(state, itemId);

	if (slot === undefined) {
		return state;
	}

	const { [slot]: _freed, ...remaining } = state.world.equippedItemIds;

	return { ...state, world: { ...state.world, equippedItemIds: remaining }};
};

/**
 * The world with one patch applied — the two-level spread every state
 * transition in the engine performs.
 *
 * @param state - The state before
 * @param patch - The world fields that move
 * @returns The state after
 */
export const withWorld = (state: RuntimeState, patch: Partial<RuntimeState['world']>): RuntimeState => ({
	...state,
	world: { ...state.world, ...patch },
});

/**
 * A membership set with one member added or removed.
 *
 * @remarks
 * D56 extracted this because it had already been written twice — once in the
 * effect applier, once in the door handler — and the switch pair would have
 * made it four. One function covers both directions, which also makes the two
 * halves of a toggle provably symmetric rather than separately correct.
 *
 * Adding is idempotent by construction: membership is a SET question, and a
 * doubled entry would make every replay's state hash depend on how many times
 * a player flipped something.
 *
 * @param members - The set, as an ordered list
 * @param member - The member moving in or out
 * @param shouldBeMember - Whether it should be in the set afterward
 * @returns The set after
 */
export const withMembership = <Member>(
	members: readonly Member[],
	member: Member,
	shouldBeMember: boolean,
): readonly Member[] => {
	if (!shouldBeMember) {
		return members.filter((existing) => existing !== member);
	}

	return members.includes(member) ? members : [...members, member];
};

/**
 * `remove_item` — the thing is gone from the world, wherever it was (D230;
 * moved here from the effect applier at its line cap, D246).
 *
 * @remarks
 * The applier's header rule has always been "removes from wherever the item is", and
 * until D230 the arm honored it for two of the four places a thing can be:
 * the hands (`inventoryItemIds`) and its authored placement
 * (`removedRoomItemIds`). It never touched `droppedItemLocations` — R25's
 * record of where the player set a thing down, which the catalog, the room
 * survey and the room's description all read — nor `equippedItemIds`, the
 * slot a worn thing occupies. So Salt Debt's `drop knife` in the Reckoner's
 * house, paying the house through a trigger whose effects were `remove_item`
 * then `set_flag`, left the knife lying in the bowl, takeable, and paying
 * the next bowl too; the pack lifted it into the hands first
 * (`add_item_to_inventory`, which does clear the drop record) and removed it
 * from there. That is a workaround for a half-kept promise. Now the effect
 * reads as the header does: a removed thing is in no room, in no hand, in no
 * slot, and on no floor.
 *
 * `removedRoomItemIds` is still written for a thing that was dropped rather
 * than placed — harmless (the catalog's placement walk never names a thing
 * no room placed) and simpler than asking where the thing came from.
 *
 * @param state - The state before
 * @param itemId - The thing removed
 * @returns The state with the thing nowhere
 */
export const withItemRemoved = (state: RuntimeState, itemId: ItemId): RuntimeState => {
	const { [itemId]: _liftedDropRoomId, ...remainingDropped } = state.world.droppedItemLocations;
	const unworn = unequipItem(state, itemId);

	return {
		...unworn,
		world: {
			...unworn.world,
			droppedItemLocations: remainingDropped,
			inventoryItemIds: unworn.world.inventoryItemIds.filter((carried) => carried !== itemId),
			removedRoomItemIds: withMembership(unworn.world.removedRoomItemIds, itemId, true),
		},
	};
};

/**
 * `add_item_to_inventory` (moved here with its sibling, D246). R25: an
 * effect-granted item is an acquisition like any other — recorded
 * once, where the player stood when it happened — and if it was lying
 * dropped somewhere, it is lifted from there (a granted item is in the
 * hands, not on two floors at once). Already carried: unchanged.
 *
 * @param state - The state BEFORE this effect
 * @param itemId - The thing granted
 * @returns The state with the item in hand
 */
export const withItemGranted = (state: RuntimeState, itemId: ItemId): RuntimeState => {
	if (state.world.inventoryItemIds.includes(itemId)) {
		return state;
	}

	const { [itemId]: _liftedRoomId, ...remainingDropped } = state.world.droppedItemLocations;

	return {
		...state,
		world: {
			...state.world,
			acquisitions: state.world.acquisitions.some((acquisition) => acquisition.itemId === itemId)
				? state.world.acquisitions
				: [
					...state.world.acquisitions,
					{
						itemId,
						roomId: state.progression.activeRoomId,
						turnCount: state.progression.turnCount,
					},
				],
			droppedItemLocations: remainingDropped,
			inventoryItemIds: [...state.world.inventoryItemIds, itemId],
			removedRoomItemIds: withMembership(state.world.removedRoomItemIds, itemId, true),
		},
	};
};
